import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rocket,
  Sparkles,
  TrendingUp,
  IndianRupee,
  ShoppingBag,
  Package,
  CreditCard,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { HERO_STATS, MARQUEE_ITEMS, SIGNUP_BONUS } from "../../lib/landing-data";
import { useAuthDialog } from "../../lib/auth-dialog-context";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", delay: i * 0.08 },
  }),
};

// Animated counter
const useCount = (target, { duration = 1400, start = 0, format = (v) => v } = {}) => {
  const [value, setValue] = useState(start);
  useEffect(() => {
    let raf;
    const begin = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - begin) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(start + (target - start) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return format(value);
};

const Counter = ({ value, prefix = "", decimals = 0 }) => {
  const v = useCount(value);
  return (
    <>
      {prefix}
      {Number(v).toLocaleString("en-IN", {
        maximumFractionDigits: decimals,
        minimumFractionDigits: decimals,
      })}
    </>
  );
};

const MetricTile = ({ icon: Icon, label, value, tint, prefix, delta }) => (
  <motion.div
    initial={{ opacity: 0, y: 12, scale: 0.96 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.5, delay: 0.4 }}
    className="relative rounded-2xl border border-slate-100 bg-white p-4 hover:shadow-md transition-shadow overflow-hidden"
  >
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${tint}`}>
      <Icon className="w-4.5 h-4.5" strokeWidth={2.2} />
    </div>
    <div className="text-xs text-slate-500 font-medium">{label}</div>
    <div className="font-heading text-2xl font-extrabold text-slate-900 mt-0.5 tabular-nums">
      <Counter value={value} prefix={prefix || ""} />
    </div>
    {delta && (
      <div className="absolute top-3 right-3 inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600">
        <ArrowUpRight className="w-3 h-3" />
        {delta}
      </div>
    )}
  </motion.div>
);

const TICKER_ROWS = [
  { id: 1, label: "Order #2143 · Meesho", amount: "₹420", platform: "M" },
  { id: 2, label: "AI listing generated · Kurti", amount: "+1", platform: "AI" },
  { id: 3, label: "Order #2144 · Flipkart", amount: "₹1,290", platform: "F" },
  { id: 4, label: "Shipping saved · ₹28", amount: "₹28", platform: "$" },
  { id: 5, label: "Order #2145 · Meesho COD", amount: "₹650", platform: "M" },
  { id: 6, label: "Listing cloned · 3 items", amount: "+3", platform: "C" },
];

const TickerRow = ({ row }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 18, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -18, scale: 0.95 }}
    transition={{ duration: 0.45, ease: "easeOut" }}
    className="flex items-center justify-between rounded-xl bg-white px-3 py-2 border border-slate-100"
  >
    <div className="flex items-center gap-2.5 min-w-0">
      <span
        className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-extrabold shrink-0 ${
          row.platform === "M"
            ? "bg-purple-100 text-purple-700"
            : row.platform === "F"
              ? "bg-amber-100 text-amber-700"
              : row.platform === "AI"
                ? "bg-fuchsia-100 text-fuchsia-700"
                : row.platform === "$"
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-violet-100 text-violet-700"
        }`}
      >
        {row.platform === "$" ? <IndianRupee className="w-3.5 h-3.5" /> : row.platform}
      </span>
      <div className="min-w-0">
        <div className="text-[11px] sm:text-xs font-semibold text-slate-800 truncate">{row.label}</div>
        <div className="text-[10px] text-slate-500">just now</div>
      </div>
    </div>
    <div className="text-xs font-bold text-emerald-600 tabular-nums whitespace-nowrap pl-2">
      {row.amount}
    </div>
  </motion.div>
);

const DashboardMockup = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 2200);
    return () => clearInterval(t);
  }, []);
  const visible = [TICKER_ROWS[tick % TICKER_ROWS.length], TICKER_ROWS[(tick + 1) % TICKER_ROWS.length], TICKER_ROWS[(tick + 2) % TICKER_ROWS.length]];

  return (
    <div className="relative rounded-3xl border border-purple-100 bg-white/95 backdrop-blur-xl shadow-2xl shadow-purple-900/10 p-5 sm:p-6 overflow-hidden">
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-purple-200/50 blur-2xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-48 h-48 rounded-full bg-fuchsia-200/40 blur-2xl pointer-events-none" />

      <div className="flex items-center justify-between mb-5 relative">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        </div>
        <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 rounded-full font-medium">
          <span className="relative w-1.5 h-1.5 mr-1.5">
            <span className="absolute inset-0 rounded-full bg-purple-600 animate-ping" />
            <span className="absolute inset-0 rounded-full bg-purple-600" />
          </span>
          Synced · Today
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 relative">
        <MetricTile icon={ShoppingBag} label="Today's Orders" value={47} tint="bg-purple-50 text-purple-700" delta="+12%" />
        <MetricTile icon={IndianRupee} label="Revenue" value={38200} tint="bg-emerald-50 text-emerald-700" prefix="₹" delta="+18%" />
        <MetricTile icon={Package} label="COD Orders" value={29} tint="bg-amber-50 text-amber-700" />
        <MetricTile icon={CreditCard} label="Prepaid" value={18} tint="bg-violet-50 text-violet-700" />
      </div>

      {/* Live activity ticker */}
      <div className="mt-5 rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50/60 to-white p-3.5 relative">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Today&rsquo;s Activity</span>
          <span className="text-[10px] text-purple-700 font-bold inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            one-tap sync
          </span>
        </div>
        <div className="space-y-1.5 relative h-[152px] overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            {visible.map((row) => (
              <TickerRow key={`${tick}-${row.id}`} row={row} />
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Platform split bars */}
      <div className="mt-4 grid grid-cols-2 gap-3 relative">
        <div className="rounded-xl border border-purple-100 bg-white p-3">
          <div className="flex justify-between text-[10px] mb-1.5">
            <span className="font-bold text-slate-700">Meesho</span>
            <span className="text-slate-500 tabular-nums">28</span>
          </div>
          <div className="h-1.5 rounded-full bg-purple-100 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "62%" }}
              transition={{ duration: 1.4, delay: 0.6, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-purple-500 to-violet-600 rounded-full"
            />
          </div>
        </div>
        <div className="rounded-xl border border-amber-100 bg-white p-3">
          <div className="flex justify-between text-[10px] mb-1.5">
            <span className="font-bold text-slate-700">Flipkart</span>
            <span className="text-slate-500 tabular-nums">19</span>
          </div>
          <div className="h-1.5 rounded-full bg-amber-100 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "38%" }}
              transition={{ duration: 1.4, delay: 0.8, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const Hero = () => {
  const { openAuth } = useAuthDialog();

  return (
    <section id="home" className="relative pt-28 lg:pt-32 pb-16 sm:pb-24 overflow-hidden grain">
      <div className="orb bg-purple-300 w-[500px] h-[500px] -top-32 -left-32" />
      <div className="orb bg-fuchsia-200 w-[420px] h-[420px] top-40 -right-20" />
      <div className="orb bg-violet-200 w-[360px] h-[360px] bottom-0 left-1/3" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}>
              <Badge
                data-testid="hero-eyebrow-badge"
                className="bg-purple-100 text-purple-700 hover:bg-purple-100 rounded-full px-3 py-1 font-semibold"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Free Chrome Extension · Built for Indian Sellers
              </Badge>
            </motion.div>

            <motion.h1
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={1}
              className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]"
            >
              List on Flipkart & Meesho{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-br from-purple-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                  10x Faster
                </span>
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="10"
                  viewBox="0 0 200 10"
                  preserveAspectRatio="none"
                >
                  <path d="M0 5 Q 50 0 100 5 T 200 5" stroke="#a855f7" strokeWidth="3" fill="none" strokeLinecap="round" />
                </svg>
              </span>{" "}
              — With AI, SEO & Smart Tools
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={2}
              className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl"
            >
              Easy Lister is a free Chrome Extension that auto-fills seller forms, generates AI-powered listings with top SEO
              keywords, tracks your daily sales dashboard, reduces Meesho shipping charges, and lets you bulk-update prices —
              all in one place.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={3}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Button
                onClick={() => openAuth("signup")}
                size="lg"
                data-testid="hero-get-started-button"
                className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-xl shadow-purple-600/30 px-7 h-12"
              >
                <Rocket className="w-4 h-4 mr-2" /> Get Started — Free
              </Button>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={3.5}
              data-testid="hero-signup-bonus"
              className="mt-5 inline-flex items-start gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-purple-50 via-fuchsia-50 to-purple-50 border border-purple-200/70 max-w-xl"
            >
              <span className="shrink-0 mt-0.5 w-8 h-8 rounded-xl bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center shadow shadow-purple-600/30">
                <Sparkles className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-purple-700">
                  {SIGNUP_BONUS.badge}
                </div>
                <div className="text-sm font-bold text-slate-900 leading-snug">
                  {SIGNUP_BONUS.title}
                </div>
                <div className="text-xs text-slate-600 mt-0.5">{SIGNUP_BONUS.sub}</div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={4}
              className="mt-10 grid grid-cols-2 sm:grid-cols-5 gap-4 max-w-2xl"
              data-testid="hero-stats-bar"
            >
              {HERO_STATS.map((s) => (
                <div key={s.label} className="text-left">
                  <div className="font-heading text-2xl sm:text-3xl font-extrabold bg-gradient-to-br from-purple-700 to-violet-600 bg-clip-text text-transparent">
                    {s.value}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-snug">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="lg:col-span-5 relative"
            data-testid="hero-dashboard-mockup"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-purple-300/40 via-violet-200/30 to-fuchsia-200/40 rounded-[2.5rem] blur-2xl pointer-events-none" />
            <div className="relative">
              <DashboardMockup />

              <div
                className="absolute -left-6 sm:-left-10 top-1/3 bg-white rounded-2xl border border-purple-100 shadow-xl p-3 hidden sm:flex items-center gap-2.5"
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <TrendingUp className="w-4.5 h-4.5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Saved Today</div>
                  <div className="font-heading font-extrabold text-slate-900 text-sm">
                    ₹<Counter value={1240} /> shipping
                  </div>
                </div>
              </div>

              <div
                className="absolute -right-4 sm:-right-8 bottom-10 bg-white rounded-2xl border border-purple-100 shadow-xl p-3 hidden sm:flex items-center gap-2.5"
              >
                <motion.div
                  animate={{ rotate: [0, 12, -12, 0], scale: [1, 1.1, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="w-9 h-9 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center"
                >
                  <Sparkles className="w-4.5 h-4.5" />
                </motion.div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">AI Generated</div>
                  <div className="font-heading font-extrabold text-slate-900 text-sm">
                    <Counter value={12} /> listings
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative mt-20 sm:mt-24 border-y border-purple-100 bg-gradient-to-r from-purple-50/40 via-white to-purple-50/40 py-4 overflow-hidden">
        <div className="flex overflow-hidden no-scrollbar">
          <div className="marquee-track flex shrink-0 gap-6 pr-6 whitespace-nowrap">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-sm font-bold text-purple-700 tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
